import type { DateValue } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import type { CreatorFilterRangesRes, VideoFilterRangesRes } from "@unbox/api";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export interface FilterRangeData {
  min: number;
  max: number;
  histogram: number[];
  count?: number;
}

export type CreatorFilterRanges = CreatorFilterRangesRes;

export const useCreatorFilterRanges = ({
  range,
  enabled = true,
}: {
  range: { start?: DateValue; end?: DateValue } | null;
  enabled?: boolean;
}) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: [
      "creator-filter-ranges",
      scope.merchantId,
      scope.workspaceId,
      range?.start?.toString(),
      range?.end?.toString(),
    ],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.creators["filter-ranges"].$get({
            param: scope.param,
            query: {
              start: range?.start?.toString() ?? "",
              end: range?.end?.toString() ?? "",
            },
          })
        : await client.merchant[":merchantId"].explorer.creators["filter-ranges"].$get({
            param: scope.param,
            query: {
              start: range?.start?.toString() ?? "",
              end: range?.end?.toString() ?? "",
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: enabled && !!scope.merchantId && !!range?.start && !!range?.end,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since ranges don't change often
  });
};

export interface ProductFilterRanges {
  gmv: FilterRangeData;
}

export const useProductFilterRanges = ({
  range,
  enabled = true,
}: {
  range: { start?: DateValue; end?: DateValue } | null;
  enabled?: boolean;
}) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: [
      "product-filter-ranges",
      scope.merchantId,
      scope.workspaceId,
      range?.start?.toString(),
      range?.end?.toString(),
    ],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.products["filter-ranges"].$get({
            param: scope.param,
            query: {
              start: range?.start?.toString() ?? "",
              end: range?.end?.toString() ?? "",
            },
          })
        : await client.merchant[":merchantId"].explorer.products["filter-ranges"].$get({
            param: scope.param,
            query: {
              start: range?.start?.toString() ?? "",
              end: range?.end?.toString() ?? "",
            },
          });
      if (!res.ok) throw await handleError(res);
      return (await res.json()) as ProductFilterRanges;
    },
    enabled: enabled && !!scope.merchantId && !!range?.start && !!range?.end,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since ranges don't change often
  });
};

export type VideoFilterRanges = VideoFilterRangesRes;

export const useVideoFilterRanges = ({
  timePeriod,
  enabled = true,
}: {
  timePeriod: "7d" | "30d" | "all_time";
  enabled?: boolean;
}) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["video-filter-ranges", scope.merchantId, scope.workspaceId, timePeriod],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.videos["filter-ranges"].$get({
            param: scope.param,
            query: {
              timePeriod,
            },
          })
        : await client.merchant[":merchantId"].explorer.videos["filter-ranges"].$get({
            param: scope.param,
            query: {
              timePeriod,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: enabled && !!scope.merchantId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since ranges don't change often
  });
};
